import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSettings } from '@/features/settings';
import {
  cancelMeasurement,
  completeMeasurement,
  setSelectedImageTools,
  selectActiveMeasurement,
  selectMeasurementStatus,
  type ActiveMeasurement,
  type MeasurementStatus,
  type SelectedImageTools,
} from '@/state/slices/measurementSession';

const VIEWER_POPUP_CHANNEL = 'viewer-popup';
const POPUP_WIDTH = 1200;
const POPUP_HEIGHT = 800;

export type ViewerPopupMessage =
  | { type: 'study-changed'; studyUuid: string }
  | { type: 'study-closed' }
  | { type: 'close-popup' }
  | { type: 'ping' }
  | { type: 'pong'; studyUuid: string }
  | { type: 'popup-ready'; studyUuid: string }
  | { type: 'popup-closing' }
  | { type: 'start-measurement'; measurement: ActiveMeasurement }
  | { type: 'complete-measurement'; value: number }
  | { type: 'cancel-measurement' }
  | { type: 'go-to-annotation'; dicomUuid: string; frameNumber: number }
  | { type: 'selected-image'; tools: SelectedImageTools | null };

function openPopupWindow(studyUuid: string): Window | null {
  const left = (window.screen.width - POPUP_WIDTH) / 2;
  const top = (window.screen.height - POPUP_HEIGHT) / 2;
  return window.open(
    `/studies/${studyUuid}/viewer`,
    'dicom-viewer',
    `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`
  );
}

/**
 * Manages the viewer popup window from the main window side.
 * Uses BroadcastChannel for bi-directional communication so the popup
 * follows study changes and survives main-window navigation.
 *
 * The poppedOutViewer setting is read-only here — it controls whether
 * the popup auto-opens once per session. Only the Settings page writes to it.
 */
export function useViewerPopup(studyUuid: string | undefined) {
  const [isDetached, setIsDetached] = useState(false);
  const { settings } = useSettings();
  const dispatch = useDispatch();
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const measurementStatus = useSelector(selectMeasurementStatus);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const studyUuidRef = useRef(studyUuid);
  const isDetachedRef = useRef(false);
  // Prevents auto-open from firing more than once per session.
  // Set after first auto-open, ping/pong detection, or manual close.
  // Resets naturally when the component unmounts (new session).
  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    studyUuidRef.current = studyUuid;
  }, [studyUuid]);

  useEffect(() => {
    isDetachedRef.current = isDetached;
  }, [isDetached]);

  // Set up BroadcastChannel and detect orphaned popups
  useEffect(() => {
    const channel = new BroadcastChannel(VIEWER_POPUP_CHANNEL);
    channelRef.current = channel;
    let hasReceivedPong = false;

    channel.onmessage = (event: MessageEvent<ViewerPopupMessage>) => {
      const msg = event.data;
      switch (msg.type) {
        case 'pong':
        case 'popup-ready': {
          hasReceivedPong = true;
          hasAutoOpenedRef.current = true;
          setIsDetached(true);
          // If popup is showing a different study, sync it
          if (studyUuidRef.current && msg.studyUuid !== studyUuidRef.current) {
            channel.postMessage({
              type: 'study-changed',
              studyUuid: studyUuidRef.current,
            });
          }
          break;
        }
        case 'popup-closing':
          setIsDetached(false);
          break;
        case 'complete-measurement':
          dispatch(completeMeasurement({ value: msg.value }));
          break;
        // The popup's viewer gates the worksheet rows.
        case 'cancel-measurement':
          dispatch(cancelMeasurement());
          break;
        case 'selected-image':
          dispatch(setSelectedImageTools(msg.tools));
          break;
        default:
          break;
      }
    };

    // Detect existing popup (e.g., after main window navigated away and back)
    channel.postMessage({ type: 'ping' });
    const retryId = setTimeout(() => {
      if (!hasReceivedPong) {
        channel.postMessage({ type: 'ping' });
      }
    }, 200);

    return () => {
      clearTimeout(retryId);
      // Tell popup the reader is no longer active
      if (isDetachedRef.current) {
        channel.postMessage({ type: 'study-closed' });
      }
      channel.close();
      channelRef.current = null;
    };
  }, [dispatch]);

  // When study changes while popup is open, tell the popup
  const prevStudyUuidRef = useRef(studyUuid);
  useEffect(() => {
    if (isDetached && studyUuid && studyUuid !== prevStudyUuidRef.current) {
      channelRef.current?.postMessage({
        type: 'study-changed',
        studyUuid,
      });
    }
    prevStudyUuidRef.current = studyUuid;
  }, [studyUuid, isDetached]);

  // Relay measurement session to popup when viewer is detached
  const prevMeasurementStatusRef = useRef<MeasurementStatus>('idle');
  useEffect(() => {
    const prevStatus = prevMeasurementStatusRef.current;
    prevMeasurementStatusRef.current = measurementStatus;

    if (!isDetachedRef.current) return;

    if (
      measurementStatus === 'awaiting' &&
      prevStatus !== 'awaiting' &&
      activeMeasurement
    ) {
      channelRef.current?.postMessage({
        type: 'start-measurement',
        measurement: activeMeasurement,
      });
    } else if (measurementStatus === 'idle' && prevStatus === 'awaiting') {
      channelRef.current?.postMessage({ type: 'cancel-measurement' });
    }
  }, [measurementStatus, activeMeasurement]);

  // Close popup when poppedOutViewer setting is toggled OFF (from Settings page)
  const prevPoppedOutRef = useRef(settings.poppedOutViewer);
  useEffect(() => {
    const wasOn = prevPoppedOutRef.current;
    const isOn = settings.poppedOutViewer;
    prevPoppedOutRef.current = isOn;

    if (wasOn && !isOn && isDetachedRef.current) {
      channelRef.current?.postMessage({ type: 'close-popup' });
    }

    // Setting toggled ON → allow auto-open again
    if (!wasOn && isOn) {
      hasAutoOpenedRef.current = false;
    }
  }, [settings.poppedOutViewer]);

  // Auto-open popup once per session when setting is ON and a study is loaded.
  // Won't re-trigger on subsequent study changes within the same session.
  useEffect(() => {
    if (
      hasAutoOpenedRef.current ||
      !settings.poppedOutViewer ||
      !studyUuid ||
      isDetachedRef.current
    ) {
      return undefined;
    }

    // Delay to let ping/pong detect an existing popup first
    const timerId = setTimeout(() => {
      hasAutoOpenedRef.current = true;
      if (!isDetachedRef.current && studyUuidRef.current) {
        const popup = openPopupWindow(studyUuidRef.current);
        if (popup) setIsDetached(true);
      }
    }, 350);
    return () => clearTimeout(timerId);
  }, [settings.poppedOutViewer, studyUuid]);

  const postGoToAnnotation = useCallback(
    (dicomUuid: string, frameNumber: number) => {
      channelRef.current?.postMessage({
        type: 'go-to-annotation',
        dicomUuid,
        frameNumber,
      });
    },
    []
  );

  const handleToggleDetach = useCallback(() => {
    if (isDetachedRef.current) {
      channelRef.current?.postMessage({ type: 'close-popup' });
      // User manually closed — don't auto-reopen for the rest of this session
      hasAutoOpenedRef.current = true;
      return;
    }

    if (studyUuidRef.current) {
      const popup = openPopupWindow(studyUuidRef.current);
      if (popup) setIsDetached(true);
    }
  }, []);

  return { isDetached, handleToggleDetach, postGoToAnnotation };
}
